import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import {
  LayoutGrid, Send, Users, ListChecks, LogOut, Settings as SettingsIcon,
  FileText, BarChart3, Sun, Moon,
} from "lucide-react";

const NavItem = ({ to, icon: Icon, label, testid }) => (
  <NavLink
    to={to}
    data-testid={testid}
    className={({ isActive }) => `cf-link ${isActive ? "cf-link-active" : ""}`}
  >
    <Icon size={14} strokeWidth={1.75} />
    <span>{label}</span>
  </NavLink>
);

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { theme, toggle, isDark } = useTheme();
  const nav = useNavigate();
  const onLogout = () => { logout(); nav("/login"); };

  return (
    <aside
      className="w-64 border-r flex flex-col h-screen sticky top-0"
      style={{ background: "var(--cf-bg)", borderColor: "var(--cf-border)" }}
    >
      <div className="px-6 py-8 border-b" style={{ borderColor: "var(--cf-border)" }}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-display text-2xl font-bold uppercase tracking-tight cf-text-base">
              COLDFORGE<span style={{ color: "var(--cf-accent)" }}>.</span>
            </div>
            <div className="font-mono text-[10px] mt-1 uppercase tracking-widest cf-text-faint">
              // outbound_os
            </div>
          </div>
          <button
            type="button"
            onClick={toggle}
            data-testid="theme-toggle"
            title={isDark ? "Switch to light theme" : "Switch to dark theme"}
            aria-label="Toggle theme"
            className="p-2 border transition-colors flex-shrink-0"
            style={{ borderColor: "var(--cf-border)", color: "var(--cf-text-muted)" }}
          >
            {isDark ? <Sun size={14} /> : <Moon size={14} />}
          </button>
        </div>
        {user?.workspace_name && (
          <div
            className="mt-4 px-3 py-2 border"
            style={{ borderColor: "var(--cf-border)", background: "var(--cf-surface)" }}
            data-testid="workspace-badge"
          >
            <div className="font-mono text-[9px] uppercase tracking-widest cf-text-faint">// workspace</div>
            <div className="text-sm font-medium mt-0.5 truncate cf-text-base">{user.workspace_name}</div>
          </div>
        )}
      </div>

      <nav className="flex-1 py-4">
        <NavItem to="/dashboard" icon={LayoutGrid} label="Dashboard" testid="nav-dashboard" />
        <NavItem to="/lists" icon={ListChecks} label="Contact Lists" testid="nav-lists" />
        <NavItem to="/campaigns" icon={Send} label="Campaigns" testid="nav-campaigns" />
        <NavItem to="/templates" icon={FileText} label="Templates" testid="nav-templates" />
        <NavItem to="/reports" icon={BarChart3} label="Reports" testid="nav-reports" />
        <NavItem to="/members" icon={Users} label="Members" testid="nav-members" />
        <NavItem to="/settings" icon={SettingsIcon} label="Settings" testid="nav-settings" />
      </nav>

      <div className="border-t px-6 py-4" style={{ borderColor: "var(--cf-border)" }}>
        <div className="text-xs font-mono uppercase tracking-wider mb-1 cf-text-dim">// signed in</div>
        <div className="text-sm truncate cf-text-base" data-testid="current-user-name">{user?.name}</div>
        <div className="text-xs truncate cf-text-dim">{user?.email}</div>
        <button
          onClick={onLogout}
          data-testid="logout-button"
          className="mt-3 cf-btn-secondary w-full justify-center"
        >
          <LogOut size={12} /> Logout
        </button>
        <div className="mt-3 font-mono text-[9px] uppercase tracking-widest cf-text-faint text-center">
          theme: <span className="cf-text-muted">{theme}</span>
        </div>
      </div>
    </aside>
  );
}
